import { HubItemElementKind, HubItemMediaKind } from "client/Api/Hub/common";
import {
	ParagraphElementSaveModel,
	SubtitleElementSaveModel,
	VideoElementSaveModel,
	EmbedElementSaveModel,
	FrequentlyAskedQuestionItemSaveModel
} from "client/Api/Hub/common.composer";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import { isNil } from "client/Misc/utils";
import { isVimeoVideoUrl } from "client/Misc/vimeo";
import { isYoutubeVideoUrl } from "client/Misc/youTube";
import {
	IValidationConfiguration,
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
} from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength,
	hasMaxSize,
	isSupportedFileType,
	isEqualTo,
	inline,
	IValidationRule
} from "client/Validation/rules";
import { IValidationRuleWithBuilder } from "client/Validation/rules/triggers";
import { localize } from "client_legacy/ndaw/culture";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import {
	HubItemColumnFormModel,
	HubItemElementFormModel,
	HubItemLayoutFormModel,
	HubItemMediaFormModel,
	HubItemRowFormModel
} from "../Management/common";

export const EMBED_CODE_MAX_LENGTH = 1024;

export function createMediaValidation(
	supportedImageTypes: readonly string[],
	pictureMaxFileSize: number
): IValidationConfiguration<HubItemMediaFormModel> {
	const result = createValidationConfiguration<HubItemMediaFormModel>();

	result.whenType(x => x.mediaKind === HubItemMediaKind.Video)
		.forScalar("videoUrl")
			.add(
				isNotNullNorUndefined(),
				isNotEmpty(),
				videoUrlValidation
			);

	result.whenType(x => x.mediaKind === HubItemMediaKind.Embed)
		.forScalar("embedCode")
			.add(
				isNotNullNorUndefined(),
				isNotEmpty(),
				doesNotExceedLength(EMBED_CODE_MAX_LENGTH)
			);

	result.whenType(x => x.mediaKind === HubItemMediaKind.Image)
		.forObject("image")
			.add(
				hasMaxSize(pictureMaxFileSize),
				isSupportedFileType(supportedImageTypes)
			);

	return result;
}

export function createLayoutValidation(
	supportedImageTypes: readonly string[],
	pictureMaxFileSize: number,
	isEmptyTextAllowed: boolean = false
): IValidationConfiguration<HubItemLayoutFormModel> {
	const result: IValidationConfiguration<HubItemLayoutFormModel> = createValidationConfiguration<HubItemLayoutFormModel>();

	const columnValidation = createValidationConfiguration<ObjectWithClientId<HubItemColumnFormModel>>();

	columnValidation.forCollection("elements")
		.forItems()
			.addValidators(
				createElementValidation(
					supportedImageTypes,
					pictureMaxFileSize,
					isEmptyTextAllowed
				)
			);

	const rowValidation = createValidationConfiguration<ObjectWithClientId<HubItemRowFormModel>>();

	rowValidation
		.forCollection("columns")
			.addValidators(columnValidation);

	result.forCollection("rows")
		.forItems()
			.addValidators(rowValidation);

	return result;
}

function createElementValidation(
	supportedImageTypes: readonly string[],
	pictureMaxFileSize: number,
	isEmptyTextAllowed: boolean = false
): IValidationConfiguration<ObjectWithClientId<HubItemElementFormModel>> {
	const result = createValidationConfiguration<ObjectWithClientId<HubItemElementFormModel>>();

	const imageValidation = result.whenType(
		(x) => x.kind === HubItemElementKind.Image
	);

	imageValidation.when((x) => isNil(x.id))
		.forObject("image")
		.forScalar("isSet")
		.add(
			isEqualTo(true)
		);

	imageValidation.forObject("image")
		.add(
			hasMaxSize(pictureMaxFileSize),
			isSupportedFileType(supportedImageTypes)
		);

	const isNotEmptyValidations: IValidationRule<string>[] = isEmptyTextAllowed
		? []
		: [
			isNotNullNorUndefined(),
			isNotEmpty()
		];

	result.whenType(
		(x): x is ObjectWithClientId<ParagraphElementSaveModel> => x.kind === HubItemElementKind.Paragraph
	)
		.forScalar("html")
			.add(...isNotEmptyValidations);

	result.whenType(
		(x): x is ObjectWithClientId<SubtitleElementSaveModel> => x.kind === HubItemElementKind.Subtitle
	)
		.forScalar("text")
			.add(
				...isNotEmptyValidations,
				doesNotExceedLength(255)
			);

	result.whenType(
		(x): x is ObjectWithClientId<VideoElementSaveModel> => x.kind === HubItemElementKind.Video
	)
		.forScalar("url")
			.add(
				isNotNullNorUndefined(),
				isNotEmpty(),
				videoUrlValidation
			);

	result.whenType(
		(x): x is ObjectWithClientId<EmbedElementSaveModel> => x.kind === HubItemElementKind.Embed
	)
		.forScalar("code")
			.add(
				isNotNullNorUndefined(),
				isNotEmpty(),
				doesNotExceedLength(EMBED_CODE_MAX_LENGTH)
			);

	const faqItemValidationConfiguration: IObjectValidatorBuilder<ObjectWithClientId<FrequentlyAskedQuestionItemSaveModel>> = result.whenType(
		(x) => x.kind === HubItemElementKind.FrequentlyAskedQuestion
	)
		.forCollection("items")
		.forItems(getClientId);

	faqItemValidationConfiguration
		.forScalar("question")
			.add(
				...isNotEmptyValidations,
				doesNotExceedLength(512)
			);

	faqItemValidationConfiguration
		.forScalar("answer")
			.add(
				...isNotEmptyValidations,
				doesNotExceedLength(4_096)
			);

	return result;
}

export const videoUrlValidation: IValidationRuleWithBuilder<string> = inline(
	(value: string): InputValidationResult => {
		if (isYoutubeVideoUrl(value) || isVimeoVideoUrl(value)) {
			return {
				kind: InputValidationResultKind.Valid
			};
		}

		return {
			kind: InputValidationResultKind.Invalid,
			errorMessage: localize("_IsNotAValidVideoURL")
		};
	}
);
